import type { CSSProperties } from "react";

/* Brand marks ported from the Claude Design prototype (pim-admin/icons.jsx).
 * In production these substitute for the live CDN logo SVGs. */

type MarkProps = {
  size?: number;
  color?: string;
  className?: string;
  style?: CSSProperties;
};

/* TK star (parent brand mark) */
export function TkStar({ size = 32, color = "var(--color-primary)", className, style }: MarkProps) {
  return (
    <svg
      viewBox="0 0 41 68"
      width={size}
      height={(size * 68) / 41}
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ flexShrink: 0, ...style }}
      aria-hidden="true"
    >
      <path d="M24.09 40.47L13.35 21.68C13.19 21.43 12.98 21.23 12.73 21.08 12.48 20.94 12.2 20.85 11.91 20.84H.91C.97 20.91 1.02 20.98 1.06 21.06L11.65 40.63C11.78 40.9 11.84 41.19 11.84 41.48 11.84 41.78 11.78 42.07 11.65 42.34L1.15 61.79H11.91C12.2 61.77 12.48 61.69 12.73 61.54 12.98 61.39 13.19 61.19 13.35 60.94L24.09 42.15C24.22 41.89 24.28 41.6 24.28 41.31 24.28 41.02 24.22 40.73 24.09 40.47" />
      <path d="M16.21 21.44L26.99 40.23C27.16 40.47 27.37 40.68 27.62 40.82 27.87 40.97 28.15 41.05 28.44 41.07H39.44C39.38 41.01 39.32 40.93 39.28 40.85L28.65 21.28C28.52 21.01 28.46 20.72 28.46 20.43 28.46 20.13 28.52 19.84 28.65 19.57L39.15.12H28.39C28.11.15 27.83.24 27.59.4 27.35.55 27.15.76 26.99 1L16.21 19.75C16.08 20.01 16.02 20.3 16.02 20.6 16.02 20.89 16.08 21.18 16.21 21.44Z" />
    </svg>
  );
}

/* Schulsuite brand mark (wing + dot symbol, no text) */
export function SchulsuiteMark({ size = 64, color = "currentColor", className, style }: MarkProps) {
  return (
    <svg
      viewBox="0 0 75 71"
      height={size}
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ flexShrink: 0, display: "block", ...style }}
      aria-hidden="true"
    >
      <path d="M60.1208 69C54.2159 69 48.8636 65.5038 46.4851 60.0925C45.9557 58.8875 44.8478 58.0344 43.5462 57.8282C43.3435 57.796 43.1395 57.7805 42.9381 57.7805C41.8444 57.7805 40.7894 58.2432 40.0482 59.0731C35.9084 63.7034 29.9829 66.3582 23.79 66.3582C11.7748 66.3569 2 56.6014 2 44.6099C2 32.6185 11.7748 22.863 23.79 22.863C28.7884 22.863 33.6746 24.6027 37.5496 27.7613C41.3743 30.8787 44.0511 35.2255 45.0867 40.0002C45.3669 41.2876 46.2837 42.3457 47.5207 42.8044C47.9598 42.9668 48.4169 43.0467 48.8714 43.0467C49.6991 43.0467 50.519 42.7813 51.1995 42.2709C53.7911 40.3263 56.8759 39.2979 60.1195 39.2979C68.3242 39.2979 74.9987 45.9592 74.9987 54.1477C74.9987 62.3361 68.3242 68.9975 60.1195 68.9975L60.1208 69Z" />
      <path d="M57.2308 12.7426C57.2308 6.85573 52.4226 2.0648 46.4914 2.04175C40.5601 2.01869 35.752 6.77224 35.752 12.6591C35.752 18.5459 40.5602 23.3369 46.4914 23.3599C52.4226 23.383 57.2308 18.6294 57.2308 12.7426Z" />
    </svg>
  );
}

/* BP brand mark — chevron-"X" glyph */
export function BpMark({ size = 28, color = "var(--color-primary)", className, style }: MarkProps) {
  return (
    <svg
      viewBox="0 0 70.87 70.87"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ flexShrink: 0, display: "block", ...style }}
      aria-hidden="true"
    >
      <path
        fill={color}
        d="M39.6 45.5 28.25 25.63c-.28-.49-.97-.89-1.53-.89h-11.6c.06.07.12.15.16.23l11.17 20.7c.27.5.27 1.3 0 1.8l-11.1 20.56h11.37c.57 0 1.25-.4 1.53-.89L39.6 47.27c.28-.49.28-1.29 0-1.78m-8.33-20.12 11.35 19.87c.28.49.97.89 1.53.89h11.6c-.06-.07-.12-.15-.16-.23L44.42 25.2c-.27-.5-.27-1.3 0-1.8l11.1-20.57H44.15c-.57 0-1.25.4-1.53.89L31.27 23.59c-.28.49-.28 1.29 0 1.78"
      />
    </svg>
  );
}

export type ProductId = "bp" | "planung" | "zeugnis" | "nextcloud";

const PRODUCT_COLOR: Record<Exclude<ProductId, "nextcloud">, string> = {
  bp: "#005f9a",
  planung: "#c89a1f",
  zeugnis: "#5a8049",
};

/* Per-product mark — TK star tinted per product; real Nextcloud mark for nextcloud. */
export function ProductMark({ id, size = 32 }: { id: ProductId; size?: number }) {
  if (id === "nextcloud") {
    return (
      <svg
        viewBox="0 0 56 16"
        width={size * 1.5}
        height={size * 0.43}
        xmlns="http://www.w3.org/2000/svg"
        style={{ flexShrink: 0, display: "block", maxWidth: "100%" }}
        aria-hidden="true"
      >
        <circle cx="8" cy="8" r="6" fill="#0082c9" />
        <circle cx="28" cy="8" r="6" fill="#0082c9" />
        <circle cx="48" cy="8" r="6" fill="#0082c9" />
      </svg>
    );
  }
  return <TkStar size={size * 0.62} color={PRODUCT_COLOR[id]} />;
}
